// src/app/components/reporting/module-identity.ts
//
// Which module a saved insight came from, and what that module looks like.
//
// ── The bug this replaces ──────────────────────────────────────────────────
//
// `MODULE_COLORS` maps a module name to ONE colour, and every tinted surface
// in this module faked its alpha by concatenating hex digits onto it:
//
//     backgroundColor: module_color + '18'      // "var(--signal-blue)18"
//     backgroundColor: qs.color + '1A'          // "var(--alpha-teal)1A"
//
// You cannot append alpha to a `var()` reference. The declaration is invalid,
// the browser drops it silently, and EVERY tinted pill in Insights & Reports
// has been rendering untinted since the day it shipped. Nothing failed, so
// nobody noticed — which is the whole argument for a token layer.
//
// The fix is a TRIPLE per module rather than a single colour: what the module
// writes in, what it tints with, and what it draws its edge with. All three
// come out of the token file, all three follow the theme, and none of them can
// be arithmetic'd at a call site.
//
// ── Why it resolves rather than looks up ───────────────────────────────────
//
// `SavedInsight.module` is a stored string and the contract is frozen, so
// whatever is already in the table stays in the table. What is in the table is
// two generations of naming: `displayModule()` in `insight-action-button.tsx`
// writes 'Antenna', 'Cognition', 'Fix Actions' and 'Analytics', while the nav
// has said 'Brand Monitoring', 'AI Reasoning Path', 'Action Items' and
// 'Performance' for some time. A plain `Record<string, …>` keyed on either
// spelling silently misses half the rows and paints them the fallback grey.
//
// So the stored value resolves to an IDENTITY, and the identity owns both the
// colour triple and the label the platform currently uses. One row saved as
// 'Antenna' and one saved as 'Brand Monitoring' are the same module and now
// look like it.
//
// ── Why the accents are these accents ──────────────────────────────────────
//
// Deep Signal is brand teal because it is the measured outcome. Reasoning is
// violet because it is model behaviour. Brand Monitoring is info blue because
// it is context. Action Items is success green because an action closes. That
// leaves Performance on gap coral, which the redesign asks for explicitly and
// which is the one assignment worth stating out loud: coral here is a module
// identity, not a status. Nothing on this page uses coral to mean an error.

export type ModuleKey =
  | "monitoring"
  | "reasoning"
  | "signal"
  | "performance"
  | "actions"
  | "general";

export interface ModuleIdentity {
  key: ModuleKey;
  /** The name the platform's nav uses today. */
  label: string;
  /** Text and dot. The legible variant, so it can carry a word as well as a dot. */
  color: string;
  /** Chip ground. */
  tint: string;
  /** Chip edge. */
  border: string;
}

const IDENTITIES: Record<ModuleKey, ModuleIdentity> = {
  monitoring: {
    key: "monitoring",
    label: "Brand Monitoring",
    color: "var(--a-info-t)",
    tint: "var(--a-info-sub)",
    border: "var(--a-info-bd)",
  },
  reasoning: {
    key: "reasoning",
    label: "AI Reasoning Path",
    color: "var(--a-reason-t)",
    tint: "var(--a-reason-sub)",
    border: "var(--a-reason-line)",
  },
  signal: {
    key: "signal",
    label: "Deep Signal",
    color: "var(--a-brand-t)",
    tint: "var(--a-brand-sub)",
    border: "var(--a-brand-line)",
  },
  performance: {
    key: "performance",
    label: "Performance",
    color: "var(--a-gap-t)",
    tint: "var(--a-gap-sub)",
    border: "var(--a-gap-line)",
  },
  actions: {
    key: "actions",
    label: "Action Items",
    color: "var(--st-ok-t)",
    tint: "var(--st-ok-bg)",
    border: "var(--st-ok-bd)",
  },
  general: {
    key: "general",
    label: "General",
    color: "var(--tx-muted)",
    tint: "var(--s-subtle)",
    border: "var(--bd-default)",
  },
};

/**
 * Every spelling that has ever been written into `SavedInsight.module`, and
 * the identity it means.
 *
 * Matched on a normalised form rather than by substring: 'Deep Signal' and
 * 'signal' both contain "signal", and so does a future 'Signal Strength'.
 * A table that can only answer for names it has seen is the honest kind — an
 * unknown name falls to `general` and renders in muted grey, which reads as
 * "a module this page does not know" rather than as a wrong colour.
 */
const ALIASES: Record<string, ModuleKey> = {
  // current
  "brand monitoring": "monitoring",
  "ai reasoning path": "reasoning",
  "deep signal": "signal",
  performance: "performance",
  "action items": "actions",
  // pre-rename, still in the table
  antenna: "monitoring",
  cognition: "reasoning",
  "prompt intelligence": "reasoning",
  "prompt tracking": "reasoning",
  "fix actions": "actions",
  analytics: "performance",
  general: "general",
};

export function moduleIdentity(stored: string | null | undefined): ModuleIdentity {
  const key = ALIASES[(stored ?? "").trim().toLowerCase()] ?? "general";
  return IDENTITIES[key];
}

/**
 * The identity for a section's `source_insight_ids` entry.
 *
 * The provenance pills on a draft section have to say which module each source
 * came from, and a section carries ids rather than modules. Resolving through
 * the insight list is the only way the pill can be TRUE — a pill that took its
 * colour from anywhere else would be a claim about another object checked
 * never, which is the defect this whole system is built to prevent.
 *
 * An id with no matching insight returns null rather than a grey pill: the
 * insight was deleted after the report was generated, and inventing a label
 * for it would be worse than saying nothing.
 */
export interface SourceRef {
  id: number;
  label: string;
  identity: ModuleIdentity;
}

export function resolveSources(
  ids: readonly number[],
  insights: readonly { id: number; title: string; module: string }[],
): SourceRef[] {
  const byId = new Map(insights.map((i) => [i.id, i]));
  const out: SourceRef[] = [];
  for (const id of ids) {
    const found = byId.get(id);
    if (!found) continue;
    out.push({ id, label: found.title, identity: moduleIdentity(found.module) });
  }
  return out;
}

/** Long titles truncate on the pill, and the cut is stated once here. */
export const SOURCE_LABEL_MAX = 44;

export function shortLabel(text: string, max: number = SOURCE_LABEL_MAX): string {
  return text.length > max ? `${text.slice(0, max - 2).trimEnd()}…` : text;
}
